import React, { useEffect } from "react";

import {
  FaChevronRight,
  FaMapMarkerAlt,
  FaMedal,
  FaTimes,
  FaUserTie,
} from "react-icons/fa";

import {
  getEventPalette,
} from "../data/calendarEventColors";

import {
  formatDayLong,
} from "../utils/calendarUtils";

import {
  getEventPublicStatus,
} from "../utils/calendarViewUtils";

export default function DayEventsDrawer({
  dayData,
  onClose,
  onSelectEvent,
}) {
  useEffect(() => {
    if (!dayData) return undefined;

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    document.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      document.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [dayData, onClose]);

  if (!dayData) return null;

  const { date, events = [] } =
    dayData;

  return (
    <div className="fixed inset-0 z-[10010]">
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        className="absolute inset-0 bg-slate-950/65 backdrop-blur-sm"
      />

      <aside className="absolute bottom-0 right-0 top-0 flex w-full flex-col bg-slate-50 shadow-[-20px_0_60px_rgba(15,23,42,0.28)] dark:bg-slate-950 sm:max-w-xl">
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 bg-white px-4 py-4 dark:border-white/10 dark:bg-slate-900">
          <div>
            <h2 className="text-lg font-black text-slate-900 dark:text-white">
              {formatDayLong(date)}
            </h2>

            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {events.length} événement
              {events.length > 1
                ? "s"
                : ""}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-white/10 dark:hover:text-white"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto p-3 sm:p-4">
          {events.map((event) => {
            const palette =
              getEventPalette(event);

            const status =
              getEventPublicStatus(
                event
              );

            return (
              <button
                key={event.id}
                type="button"
                onClick={() =>
                  onSelectEvent?.(event)
                }
                className={[
                  "relative w-full overflow-hidden rounded-2xl border p-4 text-left",
                  "transition duration-200 hover:-translate-y-0.5 hover:shadow-lg",
                  "border-[var(--calendar-border)]",
                  "bg-[var(--calendar-background)]",
                  "text-[var(--calendar-text)]",
                  "dark:border-[var(--calendar-dark-border)]",
                  "dark:bg-[var(--calendar-dark-background)]",
                  "dark:text-[var(--calendar-dark-text)]",
                  status?.key ===
                  "cancelled"
                    ? "opacity-65 grayscale"
                    : "",
                ].join(" ")}
                style={{
                  "--calendar-background":
                    palette.lightBackground,

                  "--calendar-border":
                    palette.lightBorder,

                  "--calendar-text":
                    palette.lightText,

                  "--calendar-dark-background":
                    palette.darkBackground,

                  "--calendar-dark-border":
                    palette.darkBorder,

                  "--calendar-dark-text":
                    palette.darkText,

                  "--calendar-accent":
                    palette.accent,
                }}
              >
                <span
                  className="absolute bottom-0 left-0 top-0 w-1.5 bg-[var(--calendar-accent)]"
                  aria-hidden="true"
                />

                <div className="pl-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-white/60 px-2 py-1 text-[9px] font-black uppercase tracking-wide dark:bg-black/20">
                          {palette.label}
                        </span>

                        {event.discipline_event && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-white/60 px-2 py-1 text-[9px] font-black uppercase tracking-wide dark:bg-black/20">
                            <FaMedal />

                            {
                              event.discipline_event
                            }
                          </span>
                        )}

                        {status && (
                          <span className="rounded-full bg-white/60 px-2 py-1 text-[9px] font-black uppercase tracking-wide dark:bg-black/20">
                            {status.label}
                          </span>
                        )}
                      </div>

                      <h3
                        className={[
                          "whitespace-normal break-words font-black leading-snug",
                          status?.key ===
                          "cancelled"
                            ? "line-through"
                            : "",
                        ].join(" ")}
                      >
                        {event.titre ||
                          "Événement sans titre"}
                      </h3>
                    </div>

                    <FaChevronRight className="mt-1 shrink-0 opacity-50" />
                  </div>

                  <div className="mt-3 space-y-1.5 text-xs opacity-75">
                    {event.lieu_event && (
                      <div className="flex items-start gap-2">
                        <FaMapMarkerAlt className="mt-0.5 shrink-0" />

                        <span>
                          {event.lieu_event}
                        </span>
                      </div>
                    )}

                    {event.organisateur && (
                      <div className="flex items-start gap-2">
                        <FaUserTie className="mt-0.5 shrink-0" />

                        <span>
                          {
                            event.organisateur
                          }
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </aside>
    </div>
  );
}